import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { generateClient, post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { uploadData } from "aws-amplify/storage";
import { useAuth } from "../AuthProvider";
import awsconfig from "../aws-exports";
import {
  createLomnAnalysis,
  listLomnAnalyses,
  updateLomnAnalysis,
} from "../graphql/lomnAnalysis";
import { getDocumentExportApproval } from "../graphql/idrCase";
import {
  createDocumentExportApprovalId,
  DOCUMENT_EXPORT_APPROVAL_PATH,
  hashExportContent,
  isDocumentExportApproved,
} from "../features/documentExportApproval";
import "./LOMNScreen.css";
import SendToEBridgeButton from "./SendToEBridgeButton";
import IDRWorkflowScreen from "./IDRWorkflowScreen";
import DocumentExportApprovalPanel from "./DocumentExportApprovalPanel";

const client = generateClient();

const SUPPORTED_EXTENSIONS = [
  "pdf",
  "txt",
  "docx",
  "rtf",
  "md",
  "png",
  "jpg",
  "jpeg",
  "webp",
];
const MAX_LOMN_DOCUMENT_COUNT = 10;
const MAX_LOMN_IMAGE_COUNT = 6;
const IDR_WORKFLOW_ENABLED = process.env.REACT_APP_IDR_WORKFLOW_ENABLED === "true";

const TEMPLATE_OPTIONS = [
  {
    value: "PAYER_APPEAL",
    label: "Payer Appeal",
    description: "General prior auth or denial appeal letter",
  },
  {
    value: "DME_AUTHORIZATION",
    label: "DME Authorization",
    description: "Durable medical equipment medical necessity letter",
  },
  {
    value: "IMAGING_JUSTIFICATION",
    label: "Imaging Justification",
    description: "Radiology/advanced imaging justification letter",
  },
  {
    value: "IDR_FINAL_OFFER",
    label: "IDR Final Offer",
    description:
      "Final offer letter for Federal/State surprise billing IDR cases with detailed acuity and complexity rationale",
  },
  {
    value: "MEDICAL_RECORD_REQUEST",
    label: "Medical Record Request",
    description: "Request letter for complete supporting medical records",
  },
];

const DEVELOPER_GROUP = process.env.REACT_APP_DEVELOPER_GROUP || "Developers";

const ACTIVE_ANALYSIS_STATUSES = new Set(["UPLOADING", "PROCESSING", "CANCELLING"]);

function extensionAllowed(fileName) {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  return SUPPORTED_EXTENSIONS.includes(ext);
}

function sanitizeUploadFileName(name) {
  const cleaned = String(name || "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/[\\/]+/g, "_")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned.slice(0, 180) || `document-${Date.now()}`;
}

function inferContentType(file) {
  if (file.type) return file.type;
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (ext === "pdf") return "application/pdf";
  if (ext === "txt") return "text/plain";
  if (ext === "docx") {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (ext === "rtf") return "application/rtf";
  if (ext === "md") return "text/markdown";
  if (ext === "png") return "image/png";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "webp") return "image/webp";
  return "application/octet-stream";
}

function formatEvidenceList(items) {
  if (!Array.isArray(items) || !items.length) return "None provided.";
  return items.map((item, index) => `${index + 1}. ${item}`).join("\n");
}

function parseResultJson(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function parseErrorJson(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function statusClassName(status) {
  const value = String(status || "UNKNOWN").toLowerCase();
  if (value === "completed") return "lomn-status-pill completed";
  if (value === "processing" || value === "uploading") return "lomn-status-pill processing";
  if (value === "cancelling") return "lomn-status-pill cancelling";
  if (value === "cancelled") return "lomn-status-pill cancelled";
  if (value === "failed") return "lomn-status-pill failed";
  if (value === "deleted") return "lomn-status-pill deleted";
  return "lomn-status-pill";
}

function templateLabel(templateType) {
  const matched = TEMPLATE_OPTIONS.find((item) => item.value === templateType);
  return matched?.label || "Payer Appeal";
}

function mergeFilesByFingerprint(existing, incoming) {
  const byKey = new Map();

  for (const file of Array.isArray(existing) ? existing : []) {
    const key = `${file.name}::${file.size}::${file.lastModified}`;
    byKey.set(key, file);
  }

  for (const file of Array.isArray(incoming) ? incoming : []) {
    const key = `${file.name}::${file.size}::${file.lastModified}`;
    byKey.set(key, file);
  }

  return Array.from(byKey.values());
}

function estimateProcessingMinutes(files) {
  const selected = Array.isArray(files) ? files : [];
  if (!selected.length) {
    return { minMinutes: 1, maxMinutes: 2 };
  }

  let minMinutes = 1;
  let maxMinutes = 2;

  for (const file of selected) {
    const ext = (String(file?.name || "").split(".").pop() || "").toLowerCase();
    const sizeMb = Number(file?.size || 0) / (1024 * 1024);

    // OCR-heavy PDFs and larger files typically take longer in the async worker.
    if (ext === "pdf") {
      minMinutes += 0.6;
      maxMinutes += 1.6;
      if (sizeMb >= 2) {
        minMinutes += 0.2;
        maxMinutes += 0.6;
      }
      continue;
    }

    if (["png", "jpg", "jpeg", "webp"].includes(ext)) {
      minMinutes += 0.3;
      maxMinutes += 0.9;
      continue;
    }

    minMinutes += 0.2;
    maxMinutes += 0.6;
  }

  return {
    minMinutes: Math.max(1, Math.round(minMinutes)),
    maxMinutes: Math.max(2, Math.round(maxMinutes)),
  };
}

function estimateProcessingMinutesForKeys(keys) {
  const items = Array.isArray(keys) ? keys : [];
  if (!items.length) {
    return { minMinutes: 1, maxMinutes: 2 };
  }

  let minMinutes = 1;
  let maxMinutes = 2;

  for (const key of items) {
    const ext = (String(key || "").split(".").pop() || "").toLowerCase();

    if (ext === "pdf") {
      minMinutes += 1;
      maxMinutes += 2;
      continue;
    }

    if (["png", "jpg", "jpeg", "webp"].includes(ext)) {
      minMinutes += 0.5;
      maxMinutes += 1;
      continue;
    }

    minMinutes += 0.25;
    maxMinutes += 0.75;
  }

  return {
    minMinutes: Math.max(1, Math.round(minMinutes)),
    maxMinutes: Math.max(2, Math.round(maxMinutes)),
  };
}

export default function LOMNScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showIdrWorkflow, setShowIdrWorkflow] = useState(false);
  const clinicalFileInputRef = useRef(null);
  const eobFileInputRef = useRef(null);

  const [caseName, setCaseName] = useState("");
  const [patientReference, setPatientReference] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [templateType, setTemplateType] = useState(TEMPLATE_OPTIONS[0].value);
  const [files, setFiles] = useState([]);
  const [eobFiles, setEobFiles] = useState([]);

  const [history, setHistory] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [archivingId, setArchivingId] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState("ALL");
  const [showArchived, setShowArchived] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [retryPrompt, setRetryPrompt] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [exportApproval, setExportApproval] = useState(null);
  const [exportContentHash, setExportContentHash] = useState("");
  const [exportApprovalConfirmed, setExportApprovalConfirmed] = useState(false);
  const [exportApprovalLoading, setExportApprovalLoading] = useState(false);
  const [exportApprovalLookupLoading, setExportApprovalLookupLoading] = useState(false);

  const codingApiName = process.env.REACT_APP_CODING_API_NAME || "accountRequestsAPI";
  const lomnSubmitPath = process.env.REACT_APP_LOMN_SUBMIT_PATH || "/lomn/submit";
  const lomnCancelPath = process.env.REACT_APP_LOMN_CANCEL_PATH || "/lomn/cancel";
  const exportApprovalPath = process.env.REACT_APP_EXPORT_APPROVAL_PATH || DOCUMENT_EXPORT_APPROVAL_PATH;

  const canUseIdrFinalOffer = useMemo(() => {
    const groups = Array.isArray(user?.groups) ? user.groups : [];
    return groups.includes(DEVELOPER_GROUP);
  }, [user?.groups]);

  const availableTemplateOptions = useMemo(() => {
    return TEMPLATE_OPTIONS.filter(
      (item) => item.value !== "IDR_FINAL_OFFER" || canUseIdrFinalOffer
    );
  }, [canUseIdrFinalOffer]);

  const hasActiveAnalyses = useMemo(
    () => history.some((item) => ACTIVE_ANALYSIS_STATUSES.has(String(item?.processingStatus || "").toUpperCase())),
    [history]
  );

  const selected = useMemo(
    () => history.find((item) => item.id === selectedId) || null,
    [history, selectedId]
  );

  const selectedResult = useMemo(() => parseResultJson(selected?.resultJson), [selected?.resultJson]);
  const selectedError = useMemo(() => parseErrorJson(selected?.errorJson), [selected?.errorJson]);
  const selectedStatusValue = useMemo(
    () => String(selected?.processingStatus || "").toUpperCase(),
    [selected?.processingStatus]
  );
  const selectedCanRetry = useMemo(() => {
    if (!selected) return false;
    if (!Array.isArray(selected.inputDocumentKeys) || !selected.inputDocumentKeys.length) return false;
    if (
      selectedStatusValue === "UPLOADING" ||
      selectedStatusValue === "PROCESSING" ||
      selectedStatusValue === "CANCELLING"
    ) {
      return false;
    }
    if (selectedStatusValue === "DELETED") return false;
    return true;
  }, [selected, selectedStatusValue]);

  const selectedCanCancel = useMemo(() => {
    if (!selected) return false;
    return selectedStatusValue === "UPLOADING" || selectedStatusValue === "PROCESSING";
  }, [selected, selectedStatusValue]);
  const isExportApproved = isDocumentExportApproved(exportApproval, {
    subjectType: "LOMN_ANALYSIS",
    subjectId: selected?.id || "",
    artifactVersion: 1,
    contentHash: exportContentHash,
  });

  const filteredHistory = useMemo(() => {
    const term = historySearch.trim().toLowerCase();
    const statusFilter = String(historyStatusFilter || "ALL").toUpperCase();

    return history.filter((item) => {
      const statusValue = String(item?.processingStatus || "UNKNOWN").toUpperCase();
      const isDeleted = statusValue === "DELETED";

      if (!showArchived && isDeleted) {
        return false;
      }

      if (statusFilter !== "ALL" && statusValue !== statusFilter) {
        return false;
      }

      if (!term) {
        return true;
      }

      const caseNameValue = String(item?.caseName || "").toLowerCase();
      const patientRefValue = String(item?.patientReference || "").toLowerCase();
      const templateValue = String(templateLabel(item?.templateType) || "").toLowerCase();
      return (
        caseNameValue.includes(term) ||
        patientRefValue.includes(term) ||
        templateValue.includes(term)
      );
    });
  }, [history, historySearch, historyStatusFilter, showArchived]);

  const fileListText = useMemo(() => {
    if (!files.length) return "No files selected";
    return `${files.length} file(s): ${files.map((f) => f.name).join(", ")}`;
  }, [files]);

  const eobFileListText = useMemo(() => {
    if (!eobFiles.length) return "No EOBs selected";
    return `${eobFiles.length} EOB file(s): ${eobFiles.map((f) => f.name).join(", ")}`;
  }, [eobFiles]);

  const processingEstimate = useMemo(
    () => estimateProcessingMinutes([...files, ...eobFiles]),
    [files, eobFiles]
  );

  const loadHistory = async () => {
    if (!user?.username) return;
    setLoadingHistory(true);

    try {
      const items = [];
      let nextToken = null;
      do {
        const res = await client.graphql({
          query: listLomnAnalyses,
          variables: {
            filter: { userId: { eq: user.username } },
            limit: 100,
            nextToken,
          },
        });

        items.push(...(res?.data?.listLOMNAnalyses?.items || []).filter(Boolean));
        nextToken = res?.data?.listLOMNAnalyses?.nextToken || null;
      } while (nextToken);
      const sorted = [...items].sort(
        (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      );

      setHistory(sorted);
      setHistoryError("");
      setSelectedId((prev) => prev || sorted[0]?.id || "");
    } catch (historyErr) {
      console.error("Failed to load LOMN history", historyErr);
      setHistoryError("Could not refresh LOMN history. Check your connection and try Refresh History.");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  useEffect(() => {
    if (!user?.username || !hasActiveAnalyses) return undefined;

    const interval = setInterval(() => {
      loadHistory();
    }, 7000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, hasActiveAnalyses]);

  useEffect(() => {
    if (templateType === "IDR_FINAL_OFFER" && !canUseIdrFinalOffer) {
      setTemplateType(TEMPLATE_OPTIONS[0].value);
    }
  }, [templateType, canUseIdrFinalOffer]);

  useEffect(() => {
    setRetryPrompt("");
  }, [selectedId]);

  useEffect(() => {
    let active = true;
    setExportApproval(null);
    setExportContentHash("");
    setExportApprovalConfirmed(false);
    if (selectedStatusValue !== "COMPLETED" || !selected?.id || !selectedResult?.letter) {
      setExportApprovalLookupLoading(false);
      return () => { active = false; };
    }
    setExportApprovalLookupLoading(true);
    hashExportContent(selectedResult.letter)
      .then(async (contentHash) => {
        if (!active) return;
        setExportContentHash(contentHash);
        const response = await client.graphql({
          query: getDocumentExportApproval,
          variables: {
            id: createDocumentExportApprovalId("LOMN_ANALYSIS", selected.id, 1, contentHash),
          },
        });
        if (response?.errors?.length) {
          throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
        }
        const approval = response?.data?.getDocumentExportApproval || null;
        if (approval && (
          approval.subjectType !== "LOMN_ANALYSIS" ||
          approval.subjectId !== selected.id ||
          Number(approval.artifactVersion) !== 1 ||
          approval.contentHash !== contentHash
        )) {
          throw new Error("The approval does not match the selected LOMN letter.");
        }
        if (active) setExportApproval(approval);
      })
      .catch((approvalError) => {
        if (active) {
          setExportApproval(null);
          setError(`Could not verify LOMN export approval: ${approvalError.message}`);
        }
      })
      .finally(() => {
        if (active) setExportApprovalLookupLoading(false);
      });
    return () => { active = false; };
  }, [selected?.id, selectedResult?.letter, selectedStatusValue]);

  useEffect(() => {
    if (templateType !== "IDR_FINAL_OFFER") {
      setEobFiles([]);
    }
  }, [templateType]);

  const onFilePick = (e, category) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    setError("");

    const invalid = picked.filter((f) => !extensionAllowed(f.name));
    if (invalid.length) {
      setError(
        `Unsupported file type(s): ${invalid
          .map((f) => f.name)
          .join(", ")}. Allowed: ${SUPPORTED_EXTENSIONS.join(", ")}`
      );
      return;
    }

    const currentFiles = category === "eob" ? eobFiles : files;
    const nextCategoryFiles = mergeFilesByFingerprint(currentFiles, picked);
    const nextFiles = category === "eob" ? files : nextCategoryFiles;
    const nextEobFiles = category === "eob" ? nextCategoryFiles : eobFiles;
    if (nextFiles.length + nextEobFiles.length > MAX_LOMN_DOCUMENT_COUNT) {
      setError(`A maximum of ${MAX_LOMN_DOCUMENT_COUNT} documents can be uploaded per request.`);
      return;
    }

    const imageCount = [...nextFiles, ...nextEobFiles].filter((file) =>
      ["png", "jpg", "jpeg", "webp"].includes((file.name.split(".").pop() || "").toLowerCase())
    ).length;
    if (imageCount > MAX_LOMN_IMAGE_COUNT) {
      setError(`A maximum of ${MAX_LOMN_IMAGE_COUNT} images can be uploaded per request.`);
      return;
    }

    if (category === "eob") {
      setEobFiles(nextCategoryFiles);
    } else {
      setFiles(nextCategoryFiles);
    }

  };

  const submitToLomnApi = async ({
    analysisId,
    userId,
    identityId,
    accessToken,
    caseName,
    patientReference,
    templateType,
    customPrompt,
    inputDocumentKeys,
  }) => {
    const response = await post({
      apiName: codingApiName,
      path: lomnSubmitPath,
      options: {
        body: {
          analysisId,
          userId,
          identityId,
          accessToken,
          caseName,
          patientReference,
          templateType,
          customPrompt,
          inputDocumentKeys,
          bucketName: awsconfig.aws_user_files_s3_bucket,
        },
        headers: {
          "Content-Type": "application/json",
        },
      },
    }).response;

    const payload = await response.body.json();
    const httpStatus = Number(response?.statusCode || response?.status || 200);

    if (httpStatus >= 400 || payload?.ok === false) {
      throw new Error(payload?.error || payload?.message || `LOMN request failed (${httpStatus}).`);
    }

    return {
      processingStatus: String(payload?.processingStatus || (httpStatus === 202 ? "PROCESSING" : "COMPLETED")).toUpperCase(),
      result: payload?.result || null,
      message:
        payload?.message ||
        (httpStatus === 202
          ? "LOMN request accepted and queued. This page will auto-refresh when complete."
          : "LOMN generated successfully."),
    };
  };

  const resetForm = () => {
    setCaseName("");
    setPatientReference("");
    setCustomPrompt("");
    setTemplateType(TEMPLATE_OPTIONS[0].value);
    setFiles([]);
    setEobFiles([]);
    setStatus("");
    setError("");
    if (clinicalFileInputRef.current) {
      clinicalFileInputRef.current.value = "";
    }
    if (eobFileInputRef.current) {
      eobFileInputRef.current.value = "";
    }
  };

  const submitForLomn = async () => {
    setError("");
    setStatus("");

    if (!user?.username) {
      setError("You must be signed in.");
      return;
    }

    if (!caseName.trim()) {
      setError("Please provide a case name.");
      return;
    }

    if (!files.length && !eobFiles.length) {
      setError("Please upload one or more clinical documents, EOBs, or images.");
      return;
    }

    if (templateType === "IDR_FINAL_OFFER" && !canUseIdrFinalOffer) {
      setError("IDR Final Offer is only available to users in the Developers Cognito group.");
      return;
    }

    setSubmitting(true);

    const analysisId = `lomn-${Date.now()}`;
    const keyPrefix = `${user.username}/lomn-docs/${analysisId}`;
    let requestAccepted = false;

    try {
      setStatus("Creating LOMN record...");
      await client.graphql({
        query: createLomnAnalysis,
        variables: {
          input: {
            id: analysisId,
            userId: user.username,
            caseName: caseName.trim(),
            patientReference: patientReference.trim() || null,
            templateType,
            customPrompt: customPrompt.trim() || null,
            inputDocumentKeys: [],
            processingStatus: "UPLOADING",
          },
        },
      });

      setStatus("Uploading LOMN documents...");
      const authSession = await fetchAuthSession();
      const identityId = authSession?.identityId || null;
      const accessToken = authSession?.tokens?.accessToken?.toString() || "";
      const uploadedKeys = [];

      for (const [category, categoryFiles] of [["clinical", files], ["eob", eobFiles]]) {
        for (const file of categoryFiles) {
          const key = `${keyPrefix}/${category}/${sanitizeUploadFileName(file.name)}`;
          const uploadResult = await uploadData({
            key,
            data: file,
            options: {
              accessLevel: "private",
              contentType: inferContentType(file),
            },
          }).result;

          uploadedKeys.push(uploadResult?.key || key);
        }
      }

      await client.graphql({
        query: updateLomnAnalysis,
        variables: {
          input: {
            id: analysisId,
            inputDocumentKeys: uploadedKeys,
            processingStatus: "PROCESSING",
          },
        },
      });

      setStatus(`Submitting ${uploadedKeys.length} document(s) for LOMN analysis...`);

      const apiResult = await submitToLomnApi({
        analysisId,
        userId: user.username,
        identityId,
        accessToken,
        caseName: caseName.trim(),
        patientReference: patientReference.trim() || null,
        templateType,
        customPrompt: customPrompt.trim() || null,
        inputDocumentKeys: uploadedKeys,
      });
      requestAccepted = true;

      // The async worker owns status/results once the request is queued.
      if (apiResult.processingStatus === "COMPLETED") {
        await client.graphql({
          query: updateLomnAnalysis,
          variables: {
            input: {
              id: analysisId,
              processingStatus: apiResult.processingStatus,
              resultJson: apiResult.result ? JSON.stringify(apiResult.result) : null,
              errorJson: null,
            },
          },
        });
      }

      resetForm();
      setStatus(`${apiResult.message} Select this request in Saved LOMN History to follow its progress.`);
      setHistorySearch("");
      setHistoryStatusFilter("ALL");
      await loadHistory();
      setSelectedId(analysisId);
    } catch (submitError) {
      console.error("LOMN submission failed", submitError);
      setStatus("");
      setError(requestAccepted
        ? "Your LOMN request was accepted, but the result could not be saved from this page. Refresh History to check its status before submitting again."
        : submitError?.message || "LOMN generation failed.");

      try {
        if (!requestAccepted) {
          await client.graphql({
            query: updateLomnAnalysis,
            variables: {
              input: {
                id: analysisId,
                processingStatus: "FAILED",
                errorJson: JSON.stringify({
                  message: submitError?.message || "LOMN submission failed",
                }),
              },
            },
          });
        }
      } catch (persistErr) {
        console.warn("Could not persist LOMN failure status", persistErr);
      }

      await loadHistory();
      setSelectedId(analysisId);
    } finally {
      setSubmitting(false);
    }
  };

  const approveSelectedForExport = async () => {
    if (
      !selected?.id ||
      selectedStatusValue !== "COMPLETED" ||
      !selectedResult?.letter ||
      !exportApprovalConfirmed
    ) {
      setError("Select a completed letter, review it, and confirm before approving local export.");
      return;
    }
    setExportApprovalLoading(true);
    setError("");
    try {
      const contentHash = await hashExportContent(selectedResult.letter);
      if (contentHash !== exportContentHash) {
        throw new Error("The selected letter changed. Refresh it and review the current version.");
      }
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString?.() || "";
      if (!accessToken) throw new Error("Your sign-in session is unavailable. Sign in again.");
      const response = await post({
        apiName: codingApiName,
        path: exportApprovalPath,
        options: {
          body: {
            subjectType: "LOMN_ANALYSIS",
            subjectId: selected.id,
            artifactVersion: 1,
            contentHash,
            confirmReview: true,
            accessToken,
          },
          headers: {
            "Content-Type": "application/json",
          },
        },
      }).response;
      const httpStatus = Number(response?.statusCode || response?.status || 200);
      const payload = await response.body.json();
      if (httpStatus >= 400 || payload?.ok !== true || !payload.approval) {
        throw new Error(payload?.error || `Approval could not be saved (${httpStatus}).`);
      }
      setExportApproval(payload.approval);
      setExportApprovalConfirmed(false);
      setStatus("This exact LOMN letter is approved for local copy only. No filing or transmission occurred.");
    } catch (approvalError) {
      setError(`Could not approve LOMN letter for local export: ${approvalError.message}`);
    } finally {
      setExportApprovalLoading(false);
    }
  };

  const copyLetter = async () => {
    if (!selectedResult?.letter) return;
    if (!isExportApproved || exportApprovalLookupLoading) {
      setError("Review and approve this exact letter before copying it.");
      return;
    }
    try {
      await navigator.clipboard.writeText(selectedResult.letter);
      setStatus("Letter copied to clipboard.");
    } catch {
      setError("Could not copy letter. Browser clipboard permission may be blocked.");
    }
  };

  const printLetter = () => {
    if (!selectedResult?.letter || !isExportApproved || exportApprovalLookupLoading) {
      setError("Review and approve this exact letter before printing it.");
      return;
    }
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      setError("Could not open the print view. Allow pop-ups for this site and try again.");
      return;
    }
    try {
      printWindow.opener = null;
      const printDocument = printWindow.document;
      printDocument.title = "Medical Necessity Letter";
      const style = printDocument.createElement("style");
      style.textContent = `
        body { margin: 0; color: #000; background: #fff; }
        pre { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere;
          font: 12pt/1.5 Georgia, serif; }
        @page { margin: 0.75in; }
        @media screen { body { max-width: 8in; margin: 2rem auto; padding: 1rem; } }
      `;
      const letter = printDocument.createElement("pre");
      letter.textContent = String(selectedResult.letter);
      printDocument.head.appendChild(style);
      printDocument.body.replaceChildren(letter);
      printWindow.focus();
      printWindow.print();
    } catch (printError) {
      setError(`Could not print letter: ${printError.message}`);
    }
  };

  const archiveAnalysis = async (analysisId) => {
    if (!analysisId || !user?.username) return;

    const target = history.find((item) => item.id === analysisId);
    if (!target) return;

    if (String(target.processingStatus || "").toUpperCase() === "DELETED") {
      setStatus("This record is already archived.");
      return;
    }

    const confirmed = window.confirm(
      `Archive LOMN \"${target.caseName || "Untitled"}\"? This is a soft delete and can be shown again with Show Archived.`
    );
    if (!confirmed) return;

    setArchivingId(analysisId);
    setError("");

    try {
      await client.graphql({
        query: updateLomnAnalysis,
        variables: {
          input: {
            id: analysisId,
            processingStatus: "DELETED",
          },
        },
      });

      setStatus("LOMN record archived.");
      setHistory((prev) => {
        const next = prev.map((item) =>
          item.id === analysisId ? { ...item, processingStatus: "DELETED" } : item
        );

        if (selectedId === analysisId) {
          const fallback = next.find(
            (item) =>
              item.id !== analysisId &&
              String(item.processingStatus || "").toUpperCase() !== "DELETED"
          );
          setSelectedId(fallback?.id || "");
        }

        return next;
      });
    } catch (archiveErr) {
      console.error("Failed to archive LOMN record", archiveErr);
      setError(archiveErr?.message || "Could not archive this LOMN record.");
    } finally {
      setArchivingId("");
    }
  };

  const retrySelectedAnalysis = async () => {
    setError("");

    if (!user?.username) {
      setError("You must be signed in.");
      return;
    }

    if (!selected) {
      setError("Select a history item first.");
      return;
    }

    if (!selectedCanRetry) {
      setError("This LOMN item cannot be retried right now.");
      return;
    }

    const sourceKeys = Array.isArray(selected.inputDocumentKeys) ? selected.inputDocumentKeys : [];
    if (!sourceKeys.length) {
      setError("This history item has no stored document keys to retry.");
      return;
    }

    const sourceTemplate = String(selected.templateType || TEMPLATE_OPTIONS[0].value);
    if (sourceTemplate === "IDR_FINAL_OFFER" && !canUseIdrFinalOffer) {
      setError("IDR Final Offer is only available to users in the Developers Cognito group.");
      return;
    }

    setRetrying(true);

    const analysisId = `lomn-${Date.now()}`;
    const existingPrompt = String(selected.customPrompt || "").trim();
    const extraPrompt = String(retryPrompt || "").trim();
    const combinedPrompt = [existingPrompt, extraPrompt].filter(Boolean).join("\n\n");
    let requestAccepted = false;

    try {
      setStatus("Creating retry LOMN record...");
      await client.graphql({
        query: createLomnAnalysis,
        variables: {
          input: {
            id: analysisId,
            userId: user.username,
            caseName: String(selected.caseName || "Untitled Case").trim(),
            patientReference: String(selected.patientReference || "").trim() || null,
            templateType: sourceTemplate,
            customPrompt: combinedPrompt || null,
            inputDocumentKeys: sourceKeys,
            processingStatus: "PROCESSING",
          },
        },
      });

      const authSession = await fetchAuthSession();
      const identityId = authSession?.identityId || null;
      const accessToken = authSession?.tokens?.accessToken?.toString() || "";

      setStatus(`Retrying LOMN with ${sourceKeys.length} existing document(s)...`);

      const apiResult = await submitToLomnApi({
        analysisId,
        userId: user.username,
        identityId,
        accessToken,
        caseName: String(selected.caseName || "Untitled Case").trim(),
        patientReference: String(selected.patientReference || "").trim() || null,
        templateType: sourceTemplate,
        customPrompt: combinedPrompt || null,
        inputDocumentKeys: sourceKeys,
      });
      requestAccepted = true;

      if (apiResult.processingStatus === "COMPLETED") {
        await client.graphql({
          query: updateLomnAnalysis,
          variables: {
            input: {
              id: analysisId,
              processingStatus: apiResult.processingStatus,
              resultJson: apiResult.result ? JSON.stringify(apiResult.result) : null,
              errorJson: null,
            },
          },
        });
      }

      setStatus(apiResult.message);
      setRetryPrompt("");
      setHistorySearch("");
      setHistoryStatusFilter("ALL");
      await loadHistory();
      setSelectedId(analysisId);
    } catch (retryErr) {
      console.error("LOMN retry failed", retryErr);
      setStatus("");
      setError(requestAccepted
        ? "Your LOMN retry was accepted, but the result could not be saved from this page. Refresh History to check its status before retrying again."
        : retryErr?.message || "LOMN retry failed.");

      try {
        if (!requestAccepted) {
          await client.graphql({
            query: updateLomnAnalysis,
            variables: {
              input: {
                id: analysisId,
                processingStatus: "FAILED",
                errorJson: JSON.stringify({
                  message: retryErr?.message || "LOMN retry failed",
                }),
              },
            },
          });
        }
      } catch (persistErr) {
        console.warn("Could not persist LOMN retry failure status", persistErr);
      }

      await loadHistory();
      setSelectedId(analysisId);
    } finally {
      setRetrying(false);
    }
  };

  const cancelSelectedAnalysis = async () => {
    setError("");

    if (!user?.username) {
      setError("You must be signed in.");
      return;
    }

    if (!selected) {
      setError("Select a history item first.");
      return;
    }

    if (!selectedCanCancel) {
      setError("This LOMN item cannot be cancelled right now.");
      return;
    }

    const confirmed = window.confirm(
      `Cancel in-progress LOMN "${selected.caseName || "Untitled"}"?`
    );
    if (!confirmed) return;

    setCancelling(true);

    try {
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString() || "";

      const response = await post({
        apiName: codingApiName,
        path: lomnCancelPath,
        options: {
          body: {
            analysisId: selected.id,
            accessToken,
          },
          headers: {
            "Content-Type": "application/json",
          },
        },
      }).response;

      const payload = await response.body.json();
      const httpStatus = Number(response?.statusCode || response?.status || 200);
      if (httpStatus >= 400 || payload?.ok === false) {
        throw new Error(payload?.error || payload?.message || `Cancel failed (${httpStatus}).`);
      }

      setStatus(payload?.message || "Cancellation requested.");
      setHistory((prev) =>
        prev.map((item) =>
          item.id === selected.id
            ? {
                ...item,
                processingStatus: String(payload?.processingStatus || "CANCELLED").toUpperCase(),
                errorJson: JSON.stringify({
                  code: "LOMN_CANCELLED",
                  retryable: false,
                  message: payload?.message || "Cancelled by user request.",
                }),
              }
            : item
        )
      );

      await loadHistory();
    } catch (cancelErr) {
      console.error("LOMN cancel failed", cancelErr);
      setError(cancelErr?.message || "LOMN cancel failed.");
    } finally {
      setCancelling(false);
    }
  };

  if (showIdrWorkflow || (templateType === "IDR_FINAL_OFFER" && canUseIdrFinalOffer)) {
    return <IDRWorkflowScreen onExit={() => {
      setShowIdrWorkflow(false);
      setTemplateType(TEMPLATE_OPTIONS[0].value);
    }} />;
  }

  return (
    <div className="lomn-page">
      <div className="lomn-header">
        <div>
          <h2>Letter of Medical Necessity (LOMN)</h2>
          <p className="lomn-subtitle">
            Upload documents/images, choose a template, and save generated letters to history.
          </p>
        </div>
        {IDR_WORKFLOW_ENABLED && (
          <button
            type="button"
            className="button"
            onClick={() => setShowIdrWorkflow(true)}
          >
            Open IDR Offer Preparation
          </button>
        )}
      </div>

      <div className="lomn-grid">
        <section className="lomn-card">
          <h3>New LOMN Request</h3>

          <label className="lomn-label" htmlFor="lomn-case-name">
            Case Name
          </label>
          <input
            id="lomn-case-name"
            value={caseName}
            onChange={(e) => setCaseName(e.target.value)}
            placeholder="e.g., Post-op spine MRI and PT authorization"
            disabled={submitting}
          />

          <label className="lomn-label" htmlFor="lomn-template">
            Letter Template
          </label>
          <select
            id="lomn-template"
            value={templateType}
            onChange={(e) => setTemplateType(e.target.value)}
            disabled={submitting}
          >
            {availableTemplateOptions.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <p className="lomn-muted">
            {availableTemplateOptions.find((item) => item.value === templateType)?.description}
          </p>
          {!canUseIdrFinalOffer ? (
            <p className="lomn-muted">
              IDR Final Offer is restricted to members of the Developers Cognito group.
            </p>
          ) : null}

          <label className="lomn-label" htmlFor="lomn-patient-reference">
            Patient Reference (optional)
          </label>
          <input
            id="lomn-patient-reference"
            value={patientReference}
            onChange={(e) => setPatientReference(e.target.value)}
            placeholder="Internal MRN or chart ID"
            disabled={submitting}
          />

          <div className="lomn-upload-group">
            <label className="lomn-label" htmlFor="lomn-clinical-files">
              Clinical Documents and Images
            </label>
            <input
              id="lomn-clinical-files"
              ref={clinicalFileInputRef}
              className="lomn-file-input-hidden"
              type="file"
              accept={SUPPORTED_EXTENSIONS.map((extension) => `.${extension}`).join(",")}
              multiple
              onChange={(e) => onFilePick(e, "clinical")}
              disabled={submitting}
            />
            <button
              className="button secondary"
              type="button"
              onClick={() => clinicalFileInputRef.current?.click()}
              disabled={submitting}
            >
              Upload Clinical Documents
            </button>
            <p className="lomn-muted">{fileListText}</p>
          </div>

          {templateType === "IDR_FINAL_OFFER" && (
            <div className="lomn-upload-group">
              <label className="lomn-label" htmlFor="lomn-eob-files">
                EOBs and Payment Records (optional)
              </label>
              <input
                id="lomn-eob-files"
                ref={eobFileInputRef}
                className="lomn-file-input-hidden"
                type="file"
                accept={SUPPORTED_EXTENSIONS.map((extension) => `.${extension}`).join(",")}
                multiple
                onChange={(e) => onFilePick(e, "eob")}
                disabled={submitting}
              />
              <button
                className="button secondary"
                type="button"
                onClick={() => eobFileInputRef.current?.click()}
                disabled={submitting}
              >
                Upload EOBs
              </button>
              <p className="lomn-muted">{eobFileListText}</p>
              <p className="lomn-muted">
                EOBs are marked as payment evidence for the IDR pricing analysis. Accepted:{" "}
                {SUPPORTED_EXTENSIONS.join(", ")}
              </p>
              <p className="lomn-muted">
                Switching to another template clears selected EOBs.
              </p>
            </div>
          )}
          <p className="lomn-muted">
            Maximum {MAX_LOMN_DOCUMENT_COUNT} total documents, including up to {MAX_LOMN_IMAGE_COUNT} images;
            each document must be 8 MB or smaller, with a 24 MB combined limit.
          </p>

          <label className="lomn-label" htmlFor="lomn-custom-prompt">
            Custom Prompt (optional)
          </label>
          <textarea
            id="lomn-custom-prompt"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Add payer-specific requirements, desired tone, or mandatory sections."
            rows={5}
            disabled={submitting}
          />

          <div className="lomn-actions">
            <button className="button save" onClick={submitForLomn} disabled={submitting}>
              {submitting ? "Generating..." : "Generate LOMN"}
            </button>
            <button className="button danger" onClick={resetForm} disabled={submitting}>
              Clear
            </button>
          </div>

          {status && <p className="lomn-status" role="status">{status}</p>}
          {error && <p className="lomn-error" role="alert">{error}</p>}
        </section>

        <section className="lomn-card lomn-output-card">
          <h3>Saved LOMN History</h3>
          {loadingHistory ? <p className="lomn-muted">Loading history...</p> : null}
          <button className="button" onClick={loadHistory} disabled={loadingHistory}>
            Refresh History
          </button>
          {historyError && <p className="lomn-error" role="alert">{historyError}</p>}

          <div className="lomn-history-controls">
            <input
              type="text"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              placeholder="Search case, patient ref, or template"
            />
            <select
              value={historyStatusFilter}
              onChange={(e) => setHistoryStatusFilter(e.target.value)}
            >
              <option value="ALL">All statuses</option>
              <option value="COMPLETED">Completed</option>
              <option value="PROCESSING">Processing</option>
              <option value="CANCELLING">Cancelling</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="UPLOADING">Uploading</option>
              <option value="FAILED">Failed</option>
              <option value="DELETED">Archived</option>
            </select>
            <label className="lomn-inline-check">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show Archived
            </label>
          </div>

          {!filteredHistory.length ? (
            <p className="lomn-muted">No history matches your filters.</p>
          ) : (
            <ul className="lomn-history-list">
              {filteredHistory.map((item) => {
                const active = item.id === selectedId;
                const processingStatus = String(item.processingStatus || "UNKNOWN").toUpperCase();
                const showEstimate = processingStatus === "PROCESSING" || processingStatus === "UPLOADING";
                const estimate = showEstimate
                  ? estimateProcessingMinutesForKeys(item.inputDocumentKeys)
                  : null;
                return (
                  <li
                    key={item.id}
                    className={`lomn-history-item${active ? " is-selected" : ""}`}
                    onClick={() => setSelectedId(item.id)}
                  >
                    <div className="lomn-history-title">{item.caseName || "Untitled"}</div>
                    <div className="lomn-history-meta">
                      {templateLabel(item.templateType)} | {new Date(item.createdAt || 0).toLocaleString()}
                    </div>
                    <div className="lomn-history-status-row">
                      <span className={statusClassName(item.processingStatus)}>
                        {processingStatus}
                      </span>
                      {estimate ? (
                        <span className="lomn-processing-estimate">
                          About {estimate.minMinutes}-{estimate.maxMinutes} min
                        </span>
                      ) : null}
                    </div>
                    <div className="lomn-history-actions">
                      <button
                        className="button danger lomn-archive-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveAnalysis(item.id);
                        }}
                        disabled={archivingId === item.id || String(item.processingStatus || "").toUpperCase() === "DELETED"}
                      >
                        {archivingId === item.id ? "Archiving..." : "Archive"}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <h3 className="lomn-section-heading">Selected Letter</h3>
          {!selected ? (
            <p className="lomn-muted">Select a history item to view details.</p>
          ) : (
            <>
              <div className="lomn-retry-panel">
                <label className="lomn-label" htmlFor="lomn-retry-prompt">
                  Try Again (Optional Extra Prompt)
                </label>
                <textarea
                  id="lomn-retry-prompt"
                  value={retryPrompt}
                  onChange={(e) => setRetryPrompt(e.target.value)}
                  placeholder="Add extra instructions for this retry pass without re-uploading files."
                  rows={4}
                  disabled={retrying || submitting || !selectedCanRetry}
                />
                <div className="lomn-actions">
                  <button
                    className="button danger"
                    onClick={cancelSelectedAnalysis}
                    disabled={cancelling || retrying || submitting || !selectedCanCancel}
                  >
                    {cancelling ? "Cancelling..." : "Cancel In-Progress Job"}
                  </button>
                  <button
                    className="button save"
                    onClick={retrySelectedAnalysis}
                    disabled={retrying || submitting || !selectedCanRetry}
                  >
                    {retrying ? "Retrying..." : "Try Again with Existing Documents"}
                  </button>
                </div>
                {!selectedCanRetry ? (
                  <p className="lomn-muted">
                    Retry is available for completed or failed items with stored document keys.
                  </p>
                ) : null}
                {selectedCanCancel ? (
                  <p className="lomn-muted">
                    Cancel is available while status is Uploading or Processing.
                  </p>
                ) : null}
              </div>

              {selectedResult ? (
                <>
              <DocumentExportApprovalPanel
                approved={isExportApproved}
                approvedBy={exportApproval?.approvedBy}
                approvedAt={exportApproval?.approvedAt}
                checked={exportApprovalConfirmed}
                onCheckedChange={setExportApprovalConfirmed}
                onApprove={approveSelectedForExport}
                loading={exportApprovalLoading}
                disabled={
                  !selected?.id ||
                  selectedStatusValue !== "COMPLETED" ||
                  !selectedResult?.letter ||
                  exportApprovalLookupLoading
                }
                checkboxText="I reviewed this completed letter and approve this exact version for local copy or printing."
              />
              <div className="lomn-actions">
                <button
                  className="button"
                  onClick={copyLetter}
                  disabled={!isExportApproved || exportApprovalLookupLoading}
                >
                  Copy Letter
                </button>
                <button
                  className="button"
                  onClick={printLetter}
                  disabled={!isExportApproved || exportApprovalLookupLoading}
                >
                  Print Letter
                </button>
                <SendToEBridgeButton
                  documentType="MEDICAL_NECESSITY"
                  medicalNecessityId={selected.id}
                  enabled={Boolean(selectedResult?.letter)}
                />
              </div>

              <label className="lomn-label">Medical Necessity Letter</label>
              <pre className="lomn-letter">{String(selectedResult?.letter || "")}</pre>

              <label className="lomn-label">Missing Information</label>
              <pre className="lomn-evidence">{formatEvidenceList(selectedResult?.missingInfo)}</pre>

              <label className="lomn-label">Risk Flags</label>
              <pre className="lomn-evidence">{formatEvidenceList(selectedResult?.riskFlags)}</pre>
                </>
              ) : (
                <div>
                  <p className="lomn-muted">
                    {selectedError?.message ||
                      (ACTIVE_ANALYSIS_STATUSES.has(selectedStatusValue)
                        ? "This request is still in progress. History refreshes automatically while processing."
                        : "No generated letter was stored for this request yet.")}
                  </p>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
